/**
 * Typed client for Autho's API, generated from the OpenAPI document (Architecture.md). Every shell — web,
 * desktop, mobile — talks to Autho only through this. Regenerate with `pnpm --filter @autho/api-client generate`.
 */
import createFetchClient from 'openapi-fetch';
import type { components, paths } from './schema.ts';

export type { components, paths };
export type Schemas = components['schemas'];

export const TENANT_HEADER = 'x-autho-tenant';

export interface ClientOptions {
  baseUrl?: string;
  /** Returns the current Supabase access token, refreshed by the caller (D38: auth only). */
  getToken: () => Promise<string | null>;
  /** The tenant the user is working in; omitted for /api/me and /api/tenants. */
  getTenantId?: () => string | null;
  fetch?: typeof fetch;
}

export function createApiClient(options: ClientOptions) {
  const client = createFetchClient<paths>({
    baseUrl: options.baseUrl ?? '',
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  client.use({
    async onRequest({ request }) {
      /**
       * **The profile is the one the read was asked under** (isolation finding 9, F389). It was read after the
       * token, so a read started in one profile that was still waiting for its token when the person switched went
       * out under the other profile's header — and its answer was filed under the first profile's key. Taken now,
       * before anything is awaited; and a header the caller set itself is left as it is.
       */
      const tenantId = request.headers.get(TENANT_HEADER) ?? options.getTenantId?.() ?? null;
      const token = await options.getToken();
      if (token) request.headers.set('authorization', `Bearer ${token}`);
      if (tenantId) request.headers.set(TENANT_HEADER, tenantId);
      return request;
    },
  });
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export function isApiError(value: unknown): value is ApiErrorBody {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'error' in value &&
    typeof (value as ApiErrorBody).error?.code === 'string',
  );
}
